import React, { useState, useEffect } from "react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PlotCard from "@/components/PlotCard";
import EnquiryModal from "@/components/EnquiryModal";
import PlotCompare from "@/components/PlotCompare";
import SiteMapView from "@/components/SiteMapView";
import { PageSEO } from "@/components/PageSEO";
import { PageHero } from "@/components/PageHero";
import { useLang } from "@/context/LanguageContext";
import { generatePlotBrochure } from "@/utils/pdfBrochure";
import { Filter, Search, Download, GitCompareArrows, X, Map, LayoutGrid } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLOTS_HERO = "https://static.prod-images.emergentagent.com/jobs/887722b5-7ad9-4cfc-8366-fffd7eed17f9/images/ab77224a3ec86682d71f9840eb537db3dcf3fc735d12a85599ad4cf1f5988938.jpeg";

export default function Plots() {
  const [plots, setPlots] = useState([]);
  const [sitePlots, setSitePlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sizeFilter, setSizeFilter] = useState(0);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [selectedPlot, setSelectedPlot] = useState(null);
  const [comparePlots, setComparePlots] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [viewMode, setViewMode] = useState("sitemap"); // "grid" or "sitemap"
  const { t, lang } = useLang();

  const STATUS_FILTERS = [
    { label: t("plots.allPlots"), value: "all" },
    { label: t("plots.available"), value: "available" },
    { label: t("plots.reserved"), value: "reserved" },
    { label: t("plots.sold"), value: "sold" },
  ];

  const SIZE_FILTERS = [
    { label: t("plots.allSizes"), value: 0 },
    { label: t("plots.upTo1000"), value: 1000 },
    { label: t("plots.range1kTo5k"), value: 5000 },
    { label: t("plots.above5000"), value: 999999 },
  ];

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/plots`),
      axios.get(`${API}/site-plots`),
    ]).then(([plotsRes, siteRes]) => {
      setPlots(plotsRes.data);
      setSitePlots(siteRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filteredPlots = plots.filter((p) => {
    const statusOk = statusFilter === "all" || p.status === statusFilter;
    let sizeOk = true;
    if (sizeFilter === 1000) sizeOk = p.size <= 1000;
    else if (sizeFilter === 5000) sizeOk = p.size > 1000 && p.size <= 5000;
    else if (sizeFilter === 999999) sizeOk = p.size > 5000;
    return statusOk && sizeOk;
  });

  const handleEnquire = (plot) => {
    setSelectedPlot(plot);
    setEnquiryOpen(true);
  };

  const toggleCompare = (plot) => {
    setComparePlots((prev) => {
      const exists = prev.find((p) => p.id === plot.id);
      if (exists) return prev.filter((p) => p.id !== plot.id);
      if (prev.length >= 3) return prev;
      return [...prev, plot];
    });
  };

  return (
    <div className="bg-[#0F172A]">
      <PageSEO page="plots" />
      <Navbar />

      <PageHero
        image={PLOTS_HERO}
        tag="Industrial Plots · IIE Sigaddi"
        title={t("plots.title")}
        subtitle={t("plots.subtitle")}
        breadcrumb={{ home: t("nav.home"), current: t("plots.breadcrumb") }}
      >
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => generatePlotBrochure(plots)}
            data-testid="download-brochure-btn"
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 text-sm font-bold uppercase tracking-wider transition-all duration-300 hover:shadow-[0_0_20px_rgba(249,115,22,0.3)]"
          >
            <Download className="w-4 h-4" />
            {lang === "hi" ? "PDF \u092c\u094d\u0930\u094b\u0936\u0930 \u0921\u093e\u0909\u0928\u0932\u094b\u0921" : "Download Brochure"}
          </button>
          <button
            onClick={() => setViewMode("sitemap")}
            data-testid="view-toggle-sitemap"
            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border transition-colors ${
              viewMode === "sitemap"
                ? "bg-white text-slate-900 border-white"
                : "border-white/30 text-slate-300 hover:border-orange-500 hover:text-orange-400"
            }`}
          >
            <Map className="w-4 h-4" /> Interactive Site Map
          </button>
          <button
            onClick={() => setViewMode("grid")}
            data-testid="view-toggle-grid"
            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border transition-colors ${
              viewMode === "grid"
                ? "bg-white text-slate-900 border-white"
                : "border-white/30 text-slate-300 hover:border-orange-500 hover:text-orange-400"
            }`}
          >
            <LayoutGrid className="w-4 h-4" /> Card Grid View
          </button>
        </div>
      </PageHero>

      {viewMode === "sitemap" ? (
        <SiteMapView />
      ) : (
      <>
      <div className="bg-orange-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex flex-wrap gap-6 text-sm text-white font-medium justify-center md:justify-start">
            <span>500 sqmtr — Rs.7,500/sqmtr</span>
            <span className="hidden md:inline text-orange-300">|</span>
            <span>1,000 sqmtr — Rs.7,500/sqmtr</span>
            <span className="hidden md:inline text-orange-300">|</span>
            <span>2,000 sqmtr — Rs.7,500/sqmtr</span>
            <span className="hidden md:inline text-orange-300">|</span>
            <span>3,000 sqmtr — Rs.7,500/sqmtr</span>
            <span className="hidden md:inline text-orange-300">|</span>
            <span>5,000 sqmtr — Rs.7,500/sqmtr</span>
            <span className="hidden md:inline text-orange-300">|</span>
            <span>10,000 sqmtr — Rs.7,500/sqmtr</span>
          </div>
        </div>
      </div>

      <div className="bg-[#1E293B] border-b border-white/10 sticky top-[64px] z-30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2 text-slate-400 text-sm mr-2">
            <Filter className="w-4 h-4" />
            <span>{t("plots.filter")}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setStatusFilter(f.value)}
                data-testid={`filter-status-${f.value}`}
                className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wide border transition-colors ${
                  statusFilter === f.value
                    ? "bg-white text-slate-900 border-white"
                    : "border-slate-600 text-slate-400 hover:border-orange-500 hover:text-orange-400"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 ml-4 border-l border-white/10 pl-4">
            {SIZE_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setSizeFilter(f.value)}
                data-testid={`filter-size-${f.value}`}
                className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wide border transition-colors ${
                  sizeFilter === f.value
                    ? "bg-orange-500 text-white border-orange-500"
                    : "border-slate-600 text-slate-400 hover:border-orange-500"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="ml-auto text-sm text-slate-400">
            {t("plots.showing")} <span className="font-semibold text-white">{filteredPlots.length}</span> {t("plots.plotsLabel")}
          </div>
        </div>
      </div>

      <div className="bg-[#0F172A] py-12" data-testid="plots-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-[#1E293B] border border-white/10 h-80 animate-pulse" />
              ))}
            </div>
          ) : filteredPlots.length === 0 ? (
            <div className="text-center py-20">
              <Search className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400 font-medium">{t("plots.noMatch")}</p>
              <button
                onClick={() => { setStatusFilter("all"); setSizeFilter(0); }}
                className="mt-3 text-orange-500 text-sm font-semibold hover:text-orange-600"
              >
                {t("plots.clearFilters")}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPlots.map((plot) => (
                <div key={plot.id} className="relative">
                  <PlotCard plot={plot} onEnquire={handleEnquire} />
                  {/* Compare checkbox */}
                  <button
                    onClick={() => toggleCompare(plot)}
                    data-testid={`compare-toggle-${plot.size}`}
                    className={`absolute top-3 right-24 z-10 px-2 py-1 text-xs font-bold uppercase tracking-wide flex items-center gap-1 transition-colors ${
                      comparePlots.find(p => p.id === plot.id)
                        ? "bg-blue-600 text-white"
                        : "bg-slate-800/90 text-slate-300 hover:bg-blue-900/50 border border-white/20"
                    }`}
                  >
                    <GitCompareArrows className="w-3 h-3" />
                    {comparePlots.find(p => p.id === plot.id) ? (lang === "hi" ? "\u091a\u092f\u0928\u093f\u0924" : "Selected") : (lang === "hi" ? "\u0924\u0941\u0932\u0928\u093e" : "Compare")}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Compare floating bar */}
      {comparePlots.length > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-slate-900 text-white px-6 py-3 shadow-2xl flex items-center gap-4 border border-slate-700"
          data-testid="compare-bar">
          <GitCompareArrows className="w-5 h-5 text-orange-400" />
          <span className="text-sm font-medium">
            {comparePlots.length} {lang === "hi" ? "\u092a\u094d\u0932\u0949\u091f \u091a\u092f\u0928\u093f\u0924" : "plots selected"}
          </span>
          <div className="flex gap-1">
            {comparePlots.map(p => (
              <span key={p.id} className="bg-slate-700 text-xs px-2 py-1 flex items-center gap-1">
                {p.size.toLocaleString("en-IN")} sqmtr
                <X className="w-3 h-3 cursor-pointer hover:text-red-400" onClick={() => toggleCompare(p)} />
              </span>
            ))}
          </div>
          {comparePlots.length >= 2 && (
            <button
              onClick={() => setShowCompare(true)}
              data-testid="compare-now-btn"
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors"
            >
              {lang === "hi" ? "\u0924\u0941\u0932\u0928\u093e \u0915\u0930\u0947\u0902" : "Compare Now"}
            </button>
          )}
          <button onClick={() => setComparePlots([])} className="text-slate-400 hover:text-white ml-2">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="bg-slate-900 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Individual Site Plot Sizes from PDF Map */}
          {sitePlots.length > 0 && (
            <div className="mb-10">
              <h2 className="text-white text-xl font-bold uppercase mb-4" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                All Available Plot Sizes (From Site Map)
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {sitePlots.map((sp) => (
                  <div key={sp.plot_id} className={`border p-2.5 text-center ${
                    sp.status === "available" ? "border-emerald-500/30 bg-emerald-500/5" :
                    sp.status === "reserved" ? "border-amber-500/30 bg-amber-500/5" :
                    "border-slate-600 bg-slate-800/30"
                  }`} data-testid={`site-plot-size-${sp.plot_id}`}>
                    <div className="text-white font-bold text-xs" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{sp.plot_id}</div>
                    <div className="text-orange-400 font-bold text-sm mt-0.5" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                      {sp.plot_size_sqmt?.toLocaleString()} sqmt
                    </div>
                    <div className="text-slate-500 text-xs mt-0.5">{sp.plot_type}</div>
                    <div className={`text-xs font-bold mt-1 ${
                      sp.status === "available" ? "text-emerald-400" :
                      sp.status === "reserved" ? "text-amber-400" : "text-slate-500"
                    }`}>{sp.status}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center text-white">
            <div>
              <div className="text-orange-400 font-bold text-lg mb-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>90 Years</div>
              <div className="text-slate-300 text-sm">{t("plots.leaseholdPeriod")}</div>
            </div>
            <div>
              <div className="text-orange-400 font-bold text-lg mb-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>10% + 15% + 75%</div>
              <div className="text-slate-300 text-sm">{t("plots.flexiblePayment")}</div>
            </div>
            <div>
              <div className="text-orange-400 font-bold text-lg mb-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>25% Paid</div>
              <div className="text-slate-300 text-sm">{t("plots.mortgageEligible")}</div>
            </div>
          </div>
        </div>
      </div>
      </>
      )}

      <Footer />

      <EnquiryModal
        isOpen={enquiryOpen}
        onClose={() => setEnquiryOpen(false)}
        selectedPlot={selectedPlot}
      />

      {showCompare && (
        <PlotCompare
          plots={comparePlots}
          onClose={() => setShowCompare(false)}
        />
      )}
    </div>
  );
}
